import type { RouterContext } from '@/app/routing/router-context';
import { graphqlFetch } from '@shared/api';
import { sectionQueryDocument } from './sections/sections.document';
import { Section } from '@/constants';

// Mirrors magazines.tsx/collections.tsx's own `SECTION_ID` constants --
// duplicated rather than imported since those files also pull in their
// respective (non-lazy-loaded-from-here) rendering components; this loader
// only wants the two numeric ids.
const SECTION_ID_MAGAZINES = 14;
const SECTION_ID_COLLECTIONS = 15;

/**
 * `morningstar`'s home page renders one of two sections (`Magazines` or
 * `Collections`, `magazines.tsx`/`collections.tsx`, both via `useSection`)
 * depending on a client-side `mode` toggle backed by the `section` search
 * param -- not known at loader time (`RouteSpec.loader` only gets
 * `{context, params}`, no `search`). Prefetching both is cheap (two small
 * queries) and correct regardless of which mode the reader lands on or
 * switches to, without needing to thread search params through the shared
 * loader type for this one template.
 *
 * Fire-and-forget, same reasoning as `app/routing/home-loaders.ts`'s
 * `homeIssuesLoader`: home renders real, data-independent UI regardless of
 * whether this has resolved.
 */
export function morningstarHomeLoader({ context }: { context: RouterContext }) {
	const queryClient = context.queryClient;

	for (const id of [SECTION_ID_MAGAZINES, SECTION_ID_COLLECTIONS]) {
		const sectionKey = ['sections', id, 'issues'];
		queryClient
			.ensureQueryData({
				queryKey: sectionKey,
				queryFn: async () =>
					graphqlFetch(sectionQueryDocument, { id }) as unknown as Promise<{
						section: Section | null;
					}>,
			})
			.catch(() => {});
	}
}
